import * as ecStat from "echarts-stat";

import ChartUtil from "utils/ChartUtil";
import getLogger from "utils/logger.util";
import ChartConstant from "constants/chartConstant";
import { AnalyticsStrings, TrendlineStrings } from "constants/constantStrings";
import { MEDIUM_GREY, PRIMARY_BLACK, MS_IN_A_SECOND, MINUTES_IN_AN_HOUR, HOURS_IN_A_DAY, ROUND_OFF_NUMBER} from "constants/globalConstants";

import { parseChartAlarms } from "pages/Chart/ChartHelper/ChartAlarmsUtil";

import { CIPCleaningIndexes } from "./Analytics.const";

const Logger = getLogger("AnalyticsDataParser");

const getformattedTagsData = (tags, xAxisData) => {
  const plotData = tags.map((tag, index) => {
    const { TagName, Data } = tag;
    const trace = {
      x: xAxisData,
      y: Data,
      name: TagName,
      meta: TagName,
      ...ChartUtil.getTagConfig(tag, true)
    };
    if (index > 0 && tag.mode !== "text") {
      trace.xaxis = `x${index + 1}`;
      trace.yaxis = `y${index + 1}`;
    }
    return trace;
  });
  return plotData;
};

const getLayoutConfig = (tags, range) => ({ ...ChartUtil.getDefaultChartLayout(), ...ChartUtil.getMultiPlotLayoutConfig(tags, range, 10) });

const getCIPEventsData = rawData =>
  rawData.CIP_Events.map(cip => ({
    AlarmId: `CIP ID ${cip.Id}`,
    TimeStamp: cip.End_Time,
    Enable: true
  }));

const getBarWidth = range => {
  const date1 = new Date(range[0]);
  const date2 = new Date(range[1]);
  const diffInMs = date2 - date1;
  return diffInMs / 300;
};

const getChartTrace = (tag, xAxisData, index) => {
  const trace = {
    ...ChartUtil.getTagConfig(tag, true),
    x: xAxisData,
    y: tag.Data,
    xaxis: { type: "date" },
    hovertemplate: ` <b>%{y:,.2f} (${tag.Units}) <br> %{x|%d %b %Y, %H:%M:%S} </b><extra></extra>`,
    showlegend: false
  };
  if (index > 0 && tag.mode !== "text") {
    trace.xaxis = `x${index + 1}`;
    trace.yaxis = `y${index + 1}`;
  }
  return trace;
};

const getCIPCleanigDataBarType = (tagsWithData, xAxisData, range) => {
  const width = new Array(xAxisData.length).fill(getBarWidth(range));
  const plotData = tagsWithData.map((tag, index) => ({
    ...getChartTrace(tag, xAxisData, index),
    type: "bar",
    width
  }));
  return plotData;
};

const getCIPCleanigDataScatterType = (xAxisData, tagsWithData) => tagsWithData.map((tag, index) => getChartTrace(tag, xAxisData, index));

const getLayoutCIPIndexTitle = () => ({
  text: AnalyticsStrings.CIP_NOTE,
  font: {
    family: "Noto Sans",
    size: 12,
    color: MEDIUM_GREY
  },
  xref: "paper",
  x: 0
});

const getCleaningTagsData = tagsData =>
  Object.keys(tagsData).map(tagKey => ({
    TagId: tagKey,
    Color: PRIMARY_BLACK,
    TagName: tagKey,
    Data: [...tagsData[tagKey]],
    Units: CIPCleaningIndexes[tagKey],
    Type: "markers"
  }));

export const calculateTrendLine = (xAxisData, tag, index, showLegend) => {
  const { Data, Units } = tag;
  let trendData = null;
  let previousNonNulVal;
  let startIndexWithVal = null;
  const startDate = new Date(xAxisData[0]);
  const chartData = Data.map((data, idx) => {
    const currentDate = new Date(xAxisData[idx]);
    const cumulativeMinutes = (currentDate - startDate) / (MS_IN_A_SECOND * MINUTES_IN_AN_HOUR);
    if (isNaN(parseFloat(data))) {
      data = previousNonNulVal;
    } else {
      previousNonNulVal = data;
      if (startIndexWithVal === null) {
        startIndexWithVal = idx;
      }
    }
    return [cumulativeMinutes, data];
  });
  const regression = ecStat.regression("linear", chartData);
  const gradient = regression["parameter"]["gradient"];
  const slope = (((gradient)*HOURS_IN_A_DAY * MINUTES_IN_AN_HOUR).toFixed(ROUND_OFF_NUMBER));
  const TrendlineText = TrendlineStrings.trendlineName
  .replace('{Trendline[tagId]}', `${TrendlineStrings[tag.TagId]}`) 
  .replace('{slope}', slope) 
  .replace('{unit}', tag.Units)
  const trendingYData = regression.points.map(([, point]) => parseFloat(point?.toFixed(4)));
  trendData = {
    x: xAxisData.slice(startIndexWithVal, xAxisData.length), // remove starting x axis point for initial null values
    y: trendingYData,
    hovertemplate: ` <b>%{y:,.2f} (${Units}) %{x|%d %b %Y, %H:%M:%S} </b><br>%{meta}<extra></extra>`,
    showlegend: showLegend,
    xaxis: `x${index + 1}`,
    yaxis: `y${index + 1}`,
    mode: "lines",
    line: { width: 1.5, color: "black" },
    meta: AnalyticsStrings.trendline,
    name: TrendlineText
  };
  return trendData;
};

export const parseAnalyticsData = (tagsWithData, rawData, Alarms, xAxisRange) => {
  Logger.log("parseAnalyticsData: ", tagsWithData, rawData, Alarms, xAxisRange);
  let chartPlotData = { Layout: {}, Data: [] };
  const { Time } = rawData;
  if (Time) {
    const xAxisData = Time;
    const Data = getformattedTagsData(tagsWithData, xAxisData);
    const [x1, x2] = xAxisRange;
    const range = [x1 || Time[0], x2 || Time[Time.length - 1]];
    const Layout = getLayoutConfig(tagsWithData, range);
    const cipEvents = getCIPEventsData(rawData);
    const Format = ChartConstant.CHART_PLOT_FORMATS.MULTI_PLOT;
    const verticalLinesLegend = AnalyticsStrings.VerticalLines;
    const cipEventsParsedData = parseChartAlarms(Format, tagsWithData, cipEvents, "black", verticalLinesLegend);
    const alarmsParsedData = parseChartAlarms(Format, tagsWithData, Alarms, "red", "Alarms");
    // Calculate trend lines
    const trendLines = tagsWithData
      .map((tag, index) => calculateTrendLine(xAxisData, tag, index,true))
      .filter(trendLine => trendLine !== null);

    chartPlotData = {
      Layout: { ...Layout, shapes: { ...cipEventsParsedData.shapes, ...alarmsParsedData.shapes } },
      Data: [...Data, ...trendLines, ...cipEventsParsedData.alarmsTraces, ...alarmsParsedData.alarmsTraces]
    };
  }
  return chartPlotData;
};

export const parseCIPCleanigData = (plotType, xAxisData, tagsData, xAxisRange) => {
  const plotData = { Layout: {}, Data: [] };
  if (tagsData) {
    const tagsWithData = getCleaningTagsData(tagsData);
    const title = getLayoutCIPIndexTitle();
    plotData.Layout = { title, ...getLayoutConfig(tagsWithData, xAxisRange) };
    if (plotType === "bar") {
      plotData.Data = getCIPCleanigDataBarType(tagsWithData, xAxisData, xAxisRange);
    } else {
      plotData.Data = getCIPCleanigDataScatterType(xAxisData, tagsWithData);
    }
  }
  Logger.log("parseCIPCleanigData - tagsData, plotData : ", tagsData, plotData);
  return plotData;
};

export const getCIPIndexTableData = analyticsRawData => {
  const { CIP_Events, CIP_Indexes } = analyticsRawData;

  return CIP_Events.map((data, index) => {
    Object.keys(CIP_Indexes).forEach(tagKey => {
      const cleanabilityInitial = CIP_Indexes[tagKey]["Cleanability_Intial"][index];
      data[`${tagKey}_Cleanability_Initial`] = cleanabilityInitial;
    });
    data["Display"] = true;
    return data;
  });
};
